import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";

import { resolveApiBaseUrl } from "@/lib/api/base-url";
import { detailMessage } from "@/lib/api/errors";
import { logger } from "@/lib/logger";

// Extend AxiosRequestConfig to support suppressing error toasts for background/optional calls
declare module "axios" {
  interface InternalAxiosRequestConfig {
    suppressErrorToast?: boolean;
  }
}

// API Configuration — runtime config.js (deployed images) or the Vite env
// (`npm run dev`). Precedence lives in `@/lib/api/base-url` (#847).
const API_BASE_URL = resolveApiBaseUrl();
// Legacy localStorage keys from before the credential migrations. The refresh
// token moved to an HttpOnly cookie (issue #509); the access token moved to
// module memory (issue #635); the cached user object was write-only and is no
// longer persisted. All three are scrubbed at module load and on logout so
// pre-migration sessions don't leave credentials or PII behind.
const LEGACY_ACCESS_TOKEN_KEY = "bondit_access_token";
const LEGACY_REFRESH_TOKEN_KEY = "bondit_refresh_token";
const LEGACY_USER_KEY = "bondit_user";

// Session-hint cookie (issue #640, backend counterpart in
// consultant-portal-backend). The backend sets this non-HttpOnly,
// non-sensitive cookie (value is always "1") alongside the HttpOnly refresh
// cookie on login/refresh and clears it on logout. Its only job is to let
// the bootstrap skip the guaranteed-401 /auth/refresh for anonymous
// visitors.
const SESSION_HINT_COOKIE = "bondit_has_session";

// Local fallback marker so the hint cookie is never load-bearing. Set
// whenever this frontend acquires an access token, cleared on logout and on
// a definitive 401 from /auth/refresh. It covers the deploy-order gap: if
// this frontend ships before the backend starts issuing the hint cookie (or
// the hint isn't readable cross-subdomain), sessions established here still
// attempt the bootstrap refresh instead of being silently skipped. Boolean
// only — no credential, no PII.
const PRIOR_SESSION_KEY = "bondit_prior_session";

/**
 * Dumb, dependency-free check for the backend's session-hint cookie. The
 * cookie is host/path scoped so `document.cookie` only shows it when the
 * backend issued it for an origin we can read.
 */
export const hasSessionHint = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }
  return document.cookie.split(";").some(part => part.trim().startsWith(`${SESSION_HINT_COOKIE}=`));
};

const hasPriorSessionMarker = (): boolean => {
  try {
    return localStorage.getItem(PRIOR_SESSION_KEY) === "1";
  } catch {
    return false;
  }
};

const setPriorSessionMarker = (): void => {
  try {
    localStorage.setItem(PRIOR_SESSION_KEY, "1");
  } catch {
    // Storage unavailable (private mode quota, etc.) — the marker is a
    // best-effort optimisation, never required for correctness.
  }
};

const clearPriorSessionMarker = (): void => {
  try {
    localStorage.removeItem(PRIOR_SESSION_KEY);
  } catch {
    // Ignore — see setPriorSessionMarker.
  }
};

// Keys whose values must never appear in console output. Matched
// case-insensitively against object property names. Anything matched is
// replaced with the literal string "[REDACTED]".
const SENSITIVE_KEYS = new Set([
  "authorization",
  "password",
  "current_password",
  "new_password",
  "password_confirmation",
  "refresh_token",
  "access_token",
  "token",
  "code",
  "otp",
  "reset_token",
  // PII — added in #547 logging hygiene audit. These never need to
  // surface in browser console output; redacting at the logger boundary
  // is defence-in-depth against accidental leaks via api_error dumps.
  "email",
  "username",
  "phone",
  "phone_number",
  "cpr",
  "ssn",
  "full_name",
  "first_name",
  "last_name",
  "address",
  "iban",
  "bank_account",
  "hubspot_token",
  "api_key",
]);

const REDACTED = "[REDACTED]";

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

// Masks sensitive keys on a single plain-object value, one level deep.
// Pulled out so we can reuse it from the array branch below.
const maskOneLevel = (obj: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? REDACTED : v;
  }
  return out;
};

/**
 * Returns a shallow clone of `payload` with sensitive values masked. Looks at
 * the top level and one level of nesting (covers `{ headers: { Authorization
 * }, data: { password } }` style logs **and** array-of-object shapes like
 * FastAPI's 422 `detail` envelope). Pure — never mutates the input. Non
 * plain-object, non-array inputs (null, primitives, class instances such as
 * Axios headers) are returned as-is.
 */
export const redact = (payload: unknown): unknown => {
  // Arrays at the top level: mask sensitive keys on each plain-object
  // element. FastAPI 422 responses are shaped { detail: [{ loc, msg, input?
  // }, ...] } and `input` can echo the rejected payload (e.g. a password).
  if (Array.isArray(payload)) {
    return payload.map(item => (isPlainObject(item) ? maskOneLevel(item) : item));
  }

  if (!isPlainObject(payload)) {
    return payload;
  }

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (SENSITIVE_KEYS.has(key.toLowerCase())) {
      out[key] = REDACTED;
      continue;
    }
    if (isPlainObject(value)) {
      out[key] = maskOneLevel(value);
      continue;
    }
    if (Array.isArray(value)) {
      out[key] = value.map(item => (isPlainObject(item) ? maskOneLevel(item) : item));
      continue;
    }
    out[key] = value;
  }
  return out;
};

// Axios headers can be a class instance (AxiosHeaders) — convert to a plain
// object first so the redactor can do its work.
const redactHeaders = (headers: unknown): unknown => {
  if (headers && typeof headers === "object") {
    const plain: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
      plain[key] = value;
    }
    return redact(plain);
  }
  return headers;
};

// One-time scrub: prior versions persisted credentials and the user object
// in `localStorage`. Wipe any stale entries at module load so they can't sit
// in the user's browser indefinitely after the migrations ship. Sessions
// survive this because the HttpOnly refresh cookie restores the access token
// via `attemptSessionRestore` on bootstrap.
if (typeof window !== "undefined") {
  localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  localStorage.removeItem(LEGACY_USER_KEY);
}

// Create axios instance
// `withCredentials: true` so the browser sends the HttpOnly refresh-token
// cookie (issued by the backend on /auth/login and /auth/refresh — see
// consultant-portal-backend#490) on cross-origin API calls. Requires the
// backend CORS config to echo a specific origin (not `*`) and to set
// `Access-Control-Allow-Credentials: true`.
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased to 30 seconds to handle backend processing
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Verbose request/response chatter is only useful while developing locally.
// In production it (a) clutters the console and (b) leaks sensitive tokens
// from /auth/* success responses. Gate it behind Vite's DEV flag and still
// run the values through the redactor as defence-in-depth.
const isDev = import.meta.env.DEV;

// The access token lives in module memory, never in web storage — anything
// in `localStorage`/`sessionStorage` is readable by any XSS on the page
// (issue #635). Page reloads re-acquire it from the HttpOnly refresh cookie
// via `attemptSessionRestore`.
let accessToken: string | null = null;

// Token management utilities
export const tokenManager = {
  getToken: (): string | null => {
    return accessToken;
  },

  setToken: (token: string): void => {
    accessToken = token;
    // Any acquired token proves a live session — remember that locally so
    // the next page load attempts the bootstrap refresh even if the
    // backend's session-hint cookie is missing (issue #640).
    setPriorSessionMarker();
  },

  removeToken: (): void => {
    accessToken = null;
    clearPriorSessionMarker();
    // Scrub keys persisted by sessions created before the credential
    // migrations (#509 refresh cookie, #635 in-memory access token).
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
  },
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Shared promise representing an in-flight silent refresh. Concurrent 401s
// await the same promise so /auth/refresh fires exactly once even when
// several requests expire at the same time (issue #514). It's cleared in
// `finally` so the next 401 after this refresh resolves can start a new one.
let refreshPromise: Promise<string> | null = null;

const doRefresh = async (): Promise<string> => {
  // The refresh token lives in an HttpOnly cookie (issue #509 /
  // consultant-portal-backend#490). We can't read it from JS, so the
  // call has no body and no Authorization header — `withCredentials:
  // true` is what causes the browser to attach the cookie.
  const refreshResponse = await axios.post<{ access_token: string }>("/auth/refresh", null, {
    baseURL: API_BASE_URL,
    withCredentials: true,
  });
  const newToken = refreshResponse.data?.access_token;
  if (!newToken) {
    // A 2xx without a token must fail the refresh — treating it as success
    // would poison every subsequent request with "Bearer undefined".
    throw new Error("/auth/refresh response contained no access_token");
  }
  tokenManager.setToken(newToken);
  return newToken;
};

// The backend rotates the refresh cookie on every /auth/refresh — two tabs
// refreshing concurrently would both send the same cookie and the loser
// would 401 despite a live session. The Web Locks API serializes the
// exchange across same-origin tabs; each waiter then refreshes with the
// rotated cookie. Environments without the API (jsdom) fall back to the
// per-tab call.
const doRefreshCrossTab = (): Promise<string> => {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request("bondit_token_refresh", () => doRefresh());
  }
  return doRefresh();
};

const ensureRefresh = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = doRefreshCrossTab().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/**
 * Single-flight token refresh for callers outside this module (e.g. the
 * proactive expiry timer in AuthContext). Rejects on failure so callers can
 * clear the session.
 */
export const refreshAccessToken = (): Promise<string> => ensureRefresh();

/**
 * Restore a session from the HttpOnly refresh-token cookie (issue #635). The
 * access token is memory-only, so every fresh page load starts without one;
 * this exchanges the cookie for a new access token before protected routes
 * render. Returns the token, or `null` when no valid cookie exists (user is
 * simply not logged in — not an error). Shares the single-flight
 * `refreshPromise` so a concurrent 401-triggered refresh can't double-fire.
 *
 * Issue #640: for anonymous visitors that call is a guaranteed 401 on every
 * page view, so we first check the backend's non-HttpOnly session-hint
 * cookie. No hint AND no local prior-session marker means there is nothing
 * to restore — resolve `null` with zero network calls. Either signal being
 * present (stale or not) just means we attempt the refresh exactly as
 * before, so a wrong guess can only cost one 401, never a session.
 */
export const attemptSessionRestore = async (): Promise<string | null> => {
  if (!hasSessionHint() && !hasPriorSessionMarker()) {
    return null;
  }
  try {
    return await ensureRefresh();
  } catch (error) {
    // 401 means no valid cookie — the user simply isn't logged in. Drop the
    // local marker so the next load can skip the pointless call too.
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      clearPriorSessionMarker();
      return null;
    }
    // Anything else (backend deploy, network blip) is transient: one bounded
    // retry so a reload during a hiccup doesn't dump the user on /login.
    await new Promise(resolve => setTimeout(resolve, 500));
    try {
      return await ensureRefresh();
    } catch {
      return null;
    }
  }
};

const redirectToLoginIfNeeded = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async error => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };

    // Handle 401 Unauthorized — attempt one silent token refresh.
    //
    // All concurrent 401s await the same `refreshPromise`; only the
    // first one actually hits /auth/refresh. The `_retry` flag prevents
    // a second refresh attempt if the retried request itself comes back
    // 401 (e.g. backend revoked the freshly-minted access token).
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await ensureRefresh();
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return apiClient(originalRequest);
      } catch {
        tokenManager.removeToken();
        redirectToLoginIfNeeded();
        return Promise.reject(error);
      }
    }

    // Calls that opted out (background/optional fetches) never toast, whatever
    // failed: an HTTP error, a timeout or a network error with no response.
    // Checking only the HTTP-error branch let a failed best-effort reload
    // contradict a "saved" toast when the network dropped (#920 review).
    if (originalRequest?.suppressErrorToast) {
      return Promise.reject(error);
    }

    // Handle other HTTP errors
    if (error.response) {
      const { status, data, config } = error.response;

      const redactedData = redact(data);

      if (isDev) {
        // Enhanced logging for debugging (dev only — too noisy for prod
        // and the request payload can carry tokens / PII even after
        // redaction).
        console.error(`🚨 API Error ${status}: ${config?.method?.toUpperCase()} ${config?.url}`);
        console.error("Status:", status);
        console.error("Response data:", JSON.stringify(redactedData, null, 2));
        console.error("Request URL:", config?.url);
        console.error("Request method:", config?.method);
        console.error("Request headers:", redactHeaders(config?.headers));
        if (config?.data) {
          try {
            const requestData =
              typeof config.data === "string" ? JSON.parse(config.data) : config.data;
            console.error("Request payload:", redact(requestData));
          } catch {
            console.error("Request payload (raw): [REDACTED — unparseable body]");
          }
        }
        // Never log the raw AxiosError object — its `.config` carries the
        // bearer token and the original request body.
        console.error("Full error object:", error?.message ?? "unknown");
      } else {
        // Prod: a single structured line is enough for Grafana — the
        // backend already logs the full request context with proper
        // correlation ids.
        logger.error("api_error", {
          status,
          url: config?.url,
          method: config?.method,
        });
      }

      switch (status) {
        case 400:
          if (isDev) {
            console.error("🔴 400 Bad Request Details:", {
              message: detailMessage(data?.detail) ?? "Bad request",
              fullResponse: redactedData,
              url: config?.url,
              method: config?.method,
            });
          }
          toast.error(detailMessage(data?.detail) ?? "Bad request. Please check your input.");
          break;
        case 403:
          if (isDev) {
            console.error("🔴 403 Forbidden:", redactedData);
          }
          toast.error("Access denied. You don't have permission to perform this action.");
          break;
        case 404:
          if (isDev) {
            console.error("🔴 404 Not Found:", { url: config?.url, data: redactedData });
          }
          toast.error("Resource not found.");
          break;
        case 422:
          // Validation errors from FastAPI
          if (isDev) {
            console.error("🔴 422 Validation Error:", redactedData);
          }
          if (Array.isArray(data?.detail)) {
            const errorMessages = data.detail
              .map(
                (err: { loc?: string[]; msg: string }) =>
                  `${err.loc?.join(" → ") || "Field"}: ${err.msg}`
              )
              .join("\n");
            if (isDev) {
              console.error("Validation errors breakdown:", redact(data.detail));
            }
            toast.error(`Validation Error:\n${errorMessages}`);
          } else {
            toast.error(detailMessage(data?.detail) ?? "Validation failed.");
          }
          break;
        case 500:
          if (isDev) {
            console.error("🔴 500 Server Error:", redactedData);
          }
          toast.error("Server error. Please try again later.");
          break;
        default:
          if (isDev) {
            console.error(`🔴 ${status} HTTP Error:`, redactedData);
          }
          toast.error(detailMessage(data?.detail) ?? `Request failed with status ${status}`);
      }
    } else if (error.request) {
      // Network error or timeout
      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        if (isDev) {
          console.error("⏱️ Request Timeout:", {
            timeout: apiClient.defaults.timeout,
            url: error.config?.url,
            method: error.config?.method,
            message: error.message,
          });
        } else {
          logger.error("api_timeout", {
            url: error.config?.url,
            method: error.config?.method,
          });
        }
        toast.error(
          "Request timed out. The server is taking too long to respond. Please try again."
        );
      } else {
        if (isDev) {
          console.error("🌐 Network Error:", {
            message: "No response received from server",
            url: error.config?.url,
            method: error.config?.method,
          });
        } else {
          logger.error("network_error", {
            url: error.config?.url,
            method: error.config?.method,
          });
        }
        toast.error("Network error. Please check your connection and try again.");
      }
    } else {
      // Other error
      if (isDev) {
        console.error("❌ Unknown Error:", error?.message ?? String(error));
      } else {
        logger.error("unknown_error", { message: error?.message ?? String(error) });
      }
      toast.error("An unexpected error occurred.");
    }

    return Promise.reject(error);
  }
);

// API response types
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  status: number;
}

export interface ApiError {
  detail:
    | string
    | Array<{
        loc: string[];
        msg: string;
        type: string;
      }>;
  status_code: number;
}

// Verbose request/response chatter is only useful while developing locally.
// In production it (a) clutters the console and (b) leaks sensitive tokens
// from /auth/* success responses. (`isDev` is declared once near the top.)

const logRequest = (method: string, url: string, data?: unknown): void => {
  if (!isDev) {
    return;
  }
  if (data === undefined) {
    console.warn(`📤 ${method} ${url}`);
  } else {
    console.warn(`📤 ${method} ${url}`, redact(data));
  }
};

const logResponse = (method: string, url: string, status: number, data?: unknown): void => {
  if (!isDev) {
    return;
  }
  console.warn(`📥 ${method} ${url} - ${status}`, redact(data));
};

// Helper function for making API requests
export const apiRequest = {
  get: async <T = unknown>(
    url: string,
    config?: AxiosRequestConfig & { suppressErrorToast?: boolean }
  ): Promise<T> => {
    logRequest("GET", url);
    const response = await apiClient.get<T>(url, config);
    logResponse("GET", url, response.status, response.data);
    return response.data;
  },

  post: async <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & { suppressErrorToast?: boolean }
  ): Promise<T> => {
    logRequest("POST", url, data);
    const response = await apiClient.post<T>(url, data, config);
    logResponse("POST", url, response.status, response.data);
    return response.data;
  },

  put: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    logRequest("PUT", url, data);
    const response = await apiClient.put<T>(url, data);
    logResponse("PUT", url, response.status, response.data);
    return response.data;
  },

  patch: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    logRequest("PATCH", url, data);
    const response = await apiClient.patch<T>(url, data);
    logResponse("PATCH", url, response.status, response.data);
    return response.data;
  },

  delete: async <T = unknown>(url: string, config?: { data?: unknown }): Promise<T> => {
    logRequest("DELETE", url);
    const response = await apiClient.delete<T>(url, config);
    logResponse("DELETE", url, response.status, response.data);
    return response.data;
  },
};

export default apiClient;
